import Button, { ButtonTypeEnum } from '@/components/portal/ui/button'
import { useFormik } from 'formik'
import React, { Dispatch, SetStateAction, useEffect, useState } from 'react'
import InputGroup from '../elements/InputGroup'
import RegisterHeader from './RegisterHeader'
import request, { AxiosError } from 'axios'
import * as Yup from 'yup'

import {
  CognitoIdentityProviderErrorEnum,
  cognitoErrorsIndex,
  AccountStatusEnum,
} from '@/types/cognito'
import InputCheckBox from '../elements/InputCheckbox'
import BodyOne from '@/components/portal/ui/typography/BodyOne'
import { formatPhoneNumber, testForCharacters } from '@/utils/ui-helper'
import Link from 'next/link'
import SelectDropdown from '../elements/SelectDropdown'
import { setLoadingCopy } from '@/utils/portal/setLoadingCopy'
import { event } from '@/utils/gtag'

const validationSchema = Yup.object().shape({
  email: Yup.string()
    .email('Please use a valid email address')
    .required('Required'),
  firstName: Yup.string().required('Required'),
  lastName: Yup.string().required('Required'),
  phone: Yup.string()
    .required('Required')
    .matches(/^\d{3}-\d{3}-\d{4}$/, 'Please use the format xxx-xxx-xxxx'),
  proNumber: Yup.string()
    .required('Required')
    .matches(/^[0-9]+$/, 'Must be a number'),
  company: Yup.string().required('Required'),
})

interface RegisterFormProps {
  setShowConfirmation: Dispatch<SetStateAction<boolean>>
  setSharedValues: Dispatch<
    SetStateAction<{
      email: string
    }>
  >
}

export enum AccountTypeEnum {
  PAYOR = 'PAYOR',
  CONSIGNEE = 'CONSIGNEE',
  INTERLINE = 'INTERLINE',
  BEYOND_INTERLINE = 'BEYOND_INTERLINE',
  SHIPPER = 'SHIPPER',
  AGENT = 'AGENT',
}

interface CompanyNamesProps {
  text: string
  value: string
  type: string
  is3PL: boolean
  referenceNumber: string
  accountId?: string
  associatedAccounts?: string[]
}
const RegisterForm: React.FC<RegisterFormProps> = ({
  setShowConfirmation,
  setSharedValues,
}) => {
  const [step, setStep] = useState(1)
  const [instructions, setInstructions] = useState('')
  const [companyNames, setCompanyNames] = useState<CompanyNamesProps[] | []>([])
  const [buttonCopy, setButtonCopy] = useState('')
  const [fetchedCompanyNames, setFetchedCompanyNames] = useState(false)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')

  const formik = useFormik({
    initialValues: {
      email: '',
      firstName: '',
      lastName: '',
      phone: '',
      proNumber: '',
      company: companyNames.length === 1 ? companyNames[0].value : '',
      threePLName: '',
    },
    validationSchema,
    onSubmit: async (values) => {
      const accountType =
        companyNames.find((company) => company.value === values.company)
          ?.type ?? ''

      const companyName =
        companyNames.find((company) => company.value === values.company)
          ?.text ?? ''

      const legacyID =
        companyNames.find((company) => company.value === values.company)
          ?.referenceNumber ?? undefined

      const associatedAccounts = companyNames.find(
        (company) => company.value === values.company
      )?.associatedAccounts

      const is3PL = companyNames.find(
        (company) => company.value === values.company
      )?.is3PL
      values.email = values.email.toLowerCase()
      const body = {
        ...values,
        legacyID,
        companyName,
        accountType,
        accountID: values.company,
        is3PL,
        threePLName: !testForCharacters(values.threePLName)
          ? values.threePLName
          : undefined,
        name: `${values.firstName} ${values.lastName}`,
        associatedAccounts,
      }

      try {
        const response = await request.put('/api/auth/register-user', {
          ...body,
        })

        if (
          response?.data?.status === AccountStatusEnum.PENDING &&
          response?.data?.user?.customerID
        ) {
          setShowConfirmation(true)
          if (process.env.SENTRY_ENV === 'production') {
            event({
              action: 'Successful Registration',
              category: 'Customer Portal - Register',
              label: 'A user registered for the customer portal',
              value: values.email,
            })
          }
          return setSharedValues({
            email: values.email,
          })
        } else {
          setError(
            'There was an error registering your account. Please try again.'
          )
        }
      } catch (error: AxiosError | any) {
        if (process.env.SENTRY_ENV === 'production') {
          event({
            action: 'Unsuccessful Registration',
            category: 'Customer Portal - Register',
            label: 'A user failed to register for the customer portal',
            value: values.email,
          })
        }

        setLoading(false)
        if (
          error?.response?.data?.__type &&
          error?.response?.data?.__type in cognitoErrorsIndex
        ) {
          const { __type } = error.response.data
          return setError(
            cognitoErrorsIndex[__type as CognitoIdentityProviderErrorEnum]
          )
        }
        setError(
          'There was an error processing your request. Please try again.'
        )
        console.log(error)
      }
    },
  })

  useEffect(() => {
    if (loading) {
      setLoadingCopy(setButtonCopy)
    }
  }, [loading])

  const { proNumber } = formik.values

  useEffect(() => {
    if (step === 1) {
      setInstructions(
        'Please enter a recent Pro Number so we can locate your account.'
      )
    } else if (step === 2) {
      setInstructions(
        'Please select your company name from the list below. If your company name is not listed, please contact NPT’s IT support team at'
      )
    } else {
      setInstructions('Please enter your contact information below.')
    }
  }, [step])

  useEffect(() => {
    if (fetchedCompanyNames && companyNames.length === 1) {
      formik.setFieldValue('company', companyNames[0].value)
    }
  }, [fetchedCompanyNames, companyNames])

  const handleNext = async (
    e: React.MouseEvent<HTMLDivElement, MouseEvent>
  ) => {
    e.preventDefault()
    switch (step) {
      case 1:
        {
          setLoading(true)
          const response = await request.get(
            `/api/auth/get-companies/${proNumber}`
          )
          if (response?.data?.companyNames?.length > 0) {
            setCompanyNames(response.data.companyNames)
            setLoading(false)
            setFetchedCompanyNames(true)
            setStep(2)
          } else {
            setLoading(false)
            setError(
              'Invalid Pro Number. Please try again with a different Pro Number.'
            )
          }
        }
        break
      case 2:
        {
          if (formik.values.company === '') {
            return setError('Please select a company name')
          }
          setStep(3)
        }
        break

      default:
        break
    }
  }

  const handleBack = (e: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
    e.preventDefault()
    switch (step) {
      case 2:
        formik.values.company = ''
        formik.values.threePLName = ''
        setStep(1)
        break
      case 3:
        setStep(2)
        break
      default:
        break
    }
  }

  return (
    <main className="w-full sm:w-[445px]">
      {step === 1 && <RegisterHeader />}

      <div className="w-full sm:w-[440px] mx-auto px-4">
        <BodyOne
          additionalClasses={`mb-6 ${
            step === 1
              ? 'w-full mt-2 mb-8 text-center'
              : step === 2
              ? 'mt-[14px] mb-[1.85rem] text-[1rem] text-left'
              : 'mt-[14px] mb-[2rem] ml-[1.85rem] text-[1rem] text-left'
          }`}
        >
          <>
            {instructions}
            {step === 2 && (
              <span>
                {' '}
                <a
                  href="mailto:it@nopk.biz"
                  className="text-green-portal"
                  rel="noopener noreferrer"
                >
                  it@nopk.biz
                </a>
              </span>
            )}
            {step === 3 && (
              <>
                <br />
                <span className="text-green-success font-ttHovesMedium">
                  All fields are required
                </span>
              </>
            )}
          </>
        </BodyOne>
      </div>
      <div
        className="w-full sm:w-[372px] sm:mx-[30px]"
        aria-hidden="true"
        onClick={() => setError('')}
      >
        <form onSubmit={formik.handleSubmit}>
          <div
            className={`w-[90%] sm:w-[369px] ${
              step === 1 ? 'mt-10' : 'mt-8'
            } mx-auto`}
          >
            {step === 1 && (
              <InputGroup
                label="NPT PRO Number"
                error={formik.errors.proNumber}
                placeholder="ABC123"
                value={formik.values.proNumber}
                touched={formik.touched.proNumber}
                handleBlur={formik.handleBlur('proNumber')}
                // handleChange={formik.handleChange('proNumber')}
                handleChange={(event: { target: { value: string } }) => {
                  const sanitizedValue = event.target.value.replace(
                    /[^0-9]/g,
                    ''
                  )
                  formik.setFieldValue('proNumber', sanitizedValue)
                }}
                font="font-medium text-gray-copy"
                max={15}
              />
            )}

            {step === 2 && (
              <div className="mb-[1.5rem]">
                <SelectDropdown
                  labelText="Company"
                  value={
                    companyNames.length === 1
                      ? companyNames[0].value
                      : formik.values.company
                  }
                  handleBlur={formik.handleBlur('company')}
                  handleChange={formik.handleChange('company')}
                  options={companyNames}
                  placeholder="Select a company"
                />
              </div>
            )}
            {step === 3 && (
              <>
                <div className="flex flex-col items-center justify-between w-full lg:flex-row">
                  <InputGroup
                    label="First Name"
                    error={formik.errors.firstName}
                    placeholder="John"
                    value={formik.values.firstName}
                    touched={formik.touched.firstName}
                    width="w-full lg:w-[45%]"
                    handleBlur={formik.handleBlur('firstName')}
                    handleChange={formik.handleChange('firstName')}
                    font="font-medium text-gray-copy"
                  />

                  <InputGroup
                    label="Last Name"
                    error={formik.errors.lastName}
                    placeholder="Doe"
                    value={formik.values.lastName}
                    touched={formik.touched.lastName}
                    width="w-full lg:w-[45%]"
                    handleBlur={formik.handleBlur('lastName')}
                    handleChange={formik.handleChange('lastName')}
                    font="font-medium text-gray-copy"
                  />
                </div>

                <InputGroup
                  label="Email"
                  error={formik.errors.email}
                  placeholder="you@example.com"
                  value={formik.values.email}
                  touched={formik.touched.email}
                  handleBlur={formik.handleBlur('email')}
                  handleChange={formik.handleChange('email')}
                  font="font-medium text-gray-copy"
                />

                <InputGroup
                  label="Phone number"
                  error={formik.errors.phone}
                  placeholder="XXX-XXX-XXXX"
                  value={formik.values.phone}
                  touched={formik.touched.phone}
                  handleBlur={formik.handleBlur('phone')}
                  handleChange={(event: { target: { value: string } }) => {
                    const formattedValue = formatPhoneNumber(event.target.value)
                    formik.setFieldValue('phone', formattedValue)
                  }}
                  font="font-medium text-gray-copy"
                />
              </>
            )}
            {step === 3 ? (
              <div className="flex justify-between w-full">
                <div
                  className="w-[31%]"
                  aria-hidden="true"
                  onClick={(e) => handleBack(e)}
                >
                  <Button
                    disableHoverShadow
                    additionalStyles="font-ttHovesMedium"
                    type={ButtonTypeEnum.button}
                    color="bg-gray-filter"
                    borderRadius="rounded-[4px]"
                    width="w-full"
                    inline
                  >
                    Back
                  </Button>
                </div>
                <div className="w-[31%]">
                  <Button
                    disableHoverShadow
                    additionalStyles="font-ttHovesMedium"
                    type={ButtonTypeEnum.submit}
                    borderRadius="rounded-[4px]"
                    color="bg-green-100"
                    width="w-full"
                    inline
                  >
                    Register
                  </Button>
                </div>
              </div>
            ) : (
              <div className="flex justify-between w-full">
                {step !== 1 && (
                  <div
                    aria-hidden="true"
                    className={step === 1 ? 'w-full' : 'w-[31%]'}
                    onClick={(e) => handleBack(e)}
                  >
                    <Button
                      disableHoverShadow
                      disabled={step === 1}
                      additionalStyles="font-ttHovesMedium font-gray-portal"
                      type={ButtonTypeEnum.button}
                      color="bg-white"
                      borderColor="border-gray-filter"
                      hoverColor="hover:bg-gray-light"
                      textColor="text-black"
                      width="w-full"
                      borderRadius="rounded-[4px]"
                      inline
                    >
                      Back
                    </Button>
                  </div>
                )}
                <div
                  aria-hidden="true"
                  className={step === 1 ? 'w-full mt-5' : 'w-[31%]'}
                  onClick={(e) => handleNext(e)}
                >
                  <Button
                    disableHoverShadow
                    additionalStyles="font-ttHovesMedium items-center flex justify-center"
                    type={ButtonTypeEnum.button}
                    color="bg-green-100"
                    borderRadius="rounded-[4px]"
                    width="w-full"
                    inline
                  >
                    {loading ? 'Loading' : 'Next'}
                    {loading && (
                      <span className="min-w-[20px] flex">{buttonCopy}</span>
                    )}
                  </Button>
                </div>
              </div>
            )}
            <p className="mt-2 text-red-brand text-regular">{error}</p>
          </div>
        </form>
      </div>
      <div className="flex items-center justify-center w-full mt-14">
        {step === 1 && (
          <BodyOne
            additionalClasses="mb-8 w-full text-center"
            fontSize="text-regular"
          >
            Have an account?{' '}
            <Link href="/login" className="text-green-portal">
              Sign in here.
            </Link>
          </BodyOne>
        )}
      </div>
    </main>
  )
}

export default RegisterForm
